import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import {
    TrendingUp,
    Scale,
    Ruler,
    Plus,
    History,
    ChevronLeft,
    Loader2,
    Calendar,
    ArrowUpRight,
    ArrowDownRight,
} from 'lucide-react';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';

export function AthleteProgressPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [showForm, setShowForm] = useState(false);
    const [entryType, setEntryType] = useState<'weight' | 'measurement'>('weight');

    // Form state
    const [weight, setWeight] = useState('');
    const [chest, setChest] = useState('');
    const [waist, setWaist] = useState('');
    const [hips, setHips] = useState('');

    // Fetch progress history
    const { data: entries, isLoading } = useQuery({
        queryKey: ['athlete-progress', user?.id],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('progress_entries')
                .select('*')
                .eq('user_id', user?.id)
                .order('recorded_at', { ascending: false });

            if (error) throw error;
            return data;
        },
        enabled: !!user?.id,
    });

    // Fetch workout logs for volume chart
    const { data: workoutLogs } = useQuery({
        queryKey: ['athlete-workout-logs', user?.id],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('workout_logs')
                .select(`
                    id,
                    completed_at,
                    sets:workout_log_sets(reps_completed, weight_kg)
                `)
                .eq('student_id', user?.id)
                .order('completed_at', { ascending: true });

            if (error) throw error;

            // Calculate daily volume
            const volumeData = data.reduce((acc: any[], log: any) => {
                const date = new Date(log.completed_at).toLocaleDateString('pt-BR');
                const volume = log.sets.reduce((sum: number, set: any) => sum + (set.reps_completed * set.weight_kg), 0);

                const existing = acc.find(a => a.date === date);
                if (existing) {
                    existing.volume += volume;
                } else {
                    acc.push({ date, volume });
                }
                return acc;
            }, []);

            return volumeData;
        },
        enabled: !!user?.id,
    });

    const addEntryMutation = useMutation({
        mutationFn: async (newData: any) => {
            const { error } = await supabase
                .from('progress_entries')
                .insert([{
                    user_id: user?.id,
                    recorded_at: new Date().toISOString(),
                    ...newData
                }]);
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['athlete-progress'] });
            setShowForm(false);
            resetForm();
        },
    });

    const resetForm = () => {
        setWeight('');
        setChest('');
        setWaist('');
        setHips('');
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (entryType === 'weight') {
            addEntryMutation.mutate({
                type: 'weight',
                value: parseFloat(weight),
                unit: 'kg'
            });
        } else {
            addEntryMutation.mutate({
                type: 'measurement',
                metadata: { chest, waist, hips },
                unit: 'cm'
            });
        }
    };

    const latestWeight = entries?.find(e => e.type === 'weight');
    const previousWeight = entries?.filter(e => e.type === 'weight')[1];
    const weightDiff = latestWeight && previousWeight ? latestWeight.value - previousWeight.value : null;

    // Prepare weight chart data
    const weightChartData = entries
        ?.filter(e => e.type === 'weight')
        .slice(0, 7)
        .reverse()
        .map(e => ({
            date: new Date(e.recorded_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
            peso: e.value
        }));

    return (
        <div className="max-w-4xl mx-auto space-y-6 pb-20">
            {/* Header */}
            <div className="flex items-center gap-4">
                <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors">
                    <ChevronLeft className="w-6 h-6" />
                </button>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Meu Progresso</h1>
            </div>

            {/* Charts Section */}
            <div className="grid gap-6 md:grid-cols-2">
                {/* Weight Chart */}
                <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-gray-800">
                    <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                        <Scale className="w-5 h-5 text-primary-600" />
                        Evolução de Peso
                    </h3>
                    <div className="h-64 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={weightChartData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                                <YAxis hide domain={['dataMin - 5', 'dataMax + 5']} />
                                <Tooltip
                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                                    formatter={(value: any) => [`${value} kg`, 'Peso']}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="peso"
                                    stroke="#ec4899"
                                    strokeWidth={3}
                                    dot={{ r: 4, fill: '#ec4899', strokeWidth: 2, stroke: '#fff' }}
                                    activeDot={{ r: 6, fill: '#ec4899' }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Volume Chart */}
                <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-gray-800">
                    <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-purple-600" />
                        Volume de Treino (kg)
                    </h3>
                    <div className="h-64 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={workoutLogs?.slice(-7)}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                                <YAxis hide />
                                <Tooltip
                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                                    formatter={(value: any) => [`${value} kg`, 'Volume Total']}
                                />
                                <Bar dataKey="volume" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Stats Overview */}
            <div className="grid gap-4 md:grid-cols-2">
                <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-gray-800">
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-2xl flex items-center justify-center">
                            <Scale className="w-6 h-6 text-primary-600" />
                        </div>
                        {weightDiff !== null && (
                            <span className={`flex items-center gap-1 text-sm font-bold ${weightDiff <= 0 ? 'text-emerald-500' : 'text-amber-500'}`}>
                                {weightDiff <= 0 ? <ArrowDownRight className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}
                                {Math.abs(weightDiff).toFixed(1)} kg
                            </span>
                        )}
                    </div>
                    <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Peso Atual</p>
                    <h3 className="text-3xl font-bold text-gray-900 dark:text-white">
                        {latestWeight ? `${latestWeight.value} kg` : '--'}
                    </h3>
                </div>

                <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-gray-800">
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-2xl flex items-center justify-center">
                            <TrendingUp className="w-6 h-6 text-purple-600" />
                        </div>
                    </div>
                    <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Último Registro</p>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        {latestWeight ? new Date(latestWeight.recorded_at).toLocaleDateString('pt-BR') : '--'}
                    </h3>
                </div>
            </div>

            {/* Quick Record Button */}
            {!showForm && (
                <button
                    onClick={() => setShowForm(true)}
                    className="w-full py-4 bg-primary-600 text-white rounded-2xl font-bold flex items-center justify-center gap-2 shadow-lg shadow-primary-600/20 hover:bg-primary-700 transition-all active:scale-[0.98]"
                >
                    <Plus className="w-5 h-5" />
                    Novo Registro
                </button>
            )}

            {/* Entry Form Overlay/Section */}
            {showForm && (
                <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 shadow-lg border border-primary-100 dark:border-primary-900/30 animate-in slide-in-from-bottom-4 duration-300">
                    <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                        <Plus className="w-5 h-5 text-primary-600" />
                        Registrar Evolução
                    </h2>

                    <div className="flex gap-2 p-1 bg-gray-50 dark:bg-gray-800 rounded-xl mb-6">
                        <button
                            onClick={() => setEntryType('weight')}
                            className={`flex-1 py-2 px-4 rounded-lg text-sm font-bold transition-all ${entryType === 'weight' ? 'bg-white dark:bg-gray-700 shadow-sm text-primary-600' : 'text-gray-500'}`}
                        >
                            <Scale className="w-4 h-4 inline mr-2" />
                            Peso
                        </button>
                        <button
                            onClick={() => setEntryType('measurement')}
                            className={`flex-1 py-2 px-4 rounded-lg text-sm font-bold transition-all ${entryType === 'measurement' ? 'bg-white dark:bg-gray-700 shadow-sm text-primary-600' : 'text-gray-500'}`}
                        >
                            <Ruler className="w-4 h-4 inline mr-2" />
                            Medidas
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {entryType === 'weight' ? (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Peso (kg)</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    value={weight}
                                    onChange={(e) => setWeight(e.target.value)}
                                    placeholder="Ex: 75.5"
                                    className="input text-lg py-3 rounded-xl"
                                    required
                                />
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Peito (cm)</label>
                                    <input type="number" step="0.5" value={chest} onChange={(e) => setChest(e.target.value)} className="input rounded-xl" placeholder="95" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cintura (cm)</label>
                                    <input type="number" step="0.5" value={waist} onChange={(e) => setWaist(e.target.value)} className="input rounded-xl" placeholder="80" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Quadril (cm)</label>
                                    <input type="number" step="0.5" value={hips} onChange={(e) => setHips(e.target.value)} className="input rounded-xl" placeholder="100" />
                                </div>
                            </div>
                        )}

                        <div className="flex gap-3 pt-4">
                            <button
                                type="button"
                                onClick={() => setShowForm(false)}
                                className="flex-1 py-3 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-xl font-bold"
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                disabled={addEntryMutation.isPending}
                                className="flex-2 py-3 bg-primary-600 text-white rounded-xl font-bold flex items-center justify-center gap-2"
                            >
                                {addEntryMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                                Salvar Registro
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* History List */}
            <div className="space-y-4">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <History className="w-5 h-5 text-gray-400" />
                    Histórico
                </h2>

                {isLoading ? (
                    <div className="space-y-3">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="h-20 bg-gray-50 dark:bg-gray-800/50 rounded-2xl animate-pulse"></div>
                        ))}
                    </div>
                ) : entries && entries.length > 0 ? (
                    <div className="bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 overflow-hidden shadow-sm">
                        {entries.map((entry: any, i: number) => (
                            <div
                                key={entry.id}
                                className={`flex items-center justify-between p-5 ${i !== entries.length - 1 ? 'border-b border-gray-50 dark:border-gray-800' : ''}`}
                            >
                                <div className="flex items-center gap-4">
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${entry.type === 'weight' ? 'bg-primary-50 text-primary-600' : 'bg-purple-50 text-purple-600'}`}>
                                        {entry.type === 'weight' ? <Scale className="w-5 h-5" /> : <Ruler className="w-5 h-5" />}
                                    </div>
                                    <div>
                                        <p className="font-bold text-gray-900 dark:text-white capitalize">
                                            {entry.type === 'weight' ? 'Peso' : 'Medidas'}
                                        </p>
                                        <p className="text-xs text-gray-500 flex items-center gap-1">
                                            <Calendar className="w-3 h-3" />
                                            {new Date(entry.recorded_at).toLocaleDateString('pt-BR')}
                                        </p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                                        {entry.type === 'weight' ? `${entry.value} kg` : entry.unit}
                                    </p>
                                    {entry.type === 'measurement' && (
                                        <p className="text-[10px] text-gray-400 max-w-[100px] truncate">
                                            {Object.entries(entry.metadata || {}).map(([key, val]) => `${key}: ${val}`).join(', ')}
                                        </p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/20 rounded-3xl border-2 border-dashed">
                        <p className="text-gray-500 text-sm">Nenhum registro encontrado.</p>
                    </div>
                )}
            </div>
        </div>
    );
}
